/**
 * Jyotish Tarang — "Today" (Phase 3, 10 Jul 2026 — was "Dashboard v3.0")
 * Rashifal + birth-Panchang context + current dasha indicator + live
 * Gochara/current-transits highlight, per the dynamic-only IA. This is the
 * one landing page for a chart — the old 21-tab ChartView deep-dive moved
 * into the tier-aware PDF (see ReportPage.tsx / "My Report").
 */
import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../App';
import { computePanchangLabels } from '../components/PanchangTab';

interface Chart {
  id: number; name: string; dob: string; tob: string;
  place_name: string; birth_time_conf: string;
}
interface Panchang {
  vara: string; nakshatra: string; tithi: string; yoga: string;
  rahukaal: string; moon_sign: string; auspicious_time: string;
}

const DASHA_BG: Record<string, string> = {
  Sun:'#8B4500', Moon:'#003399', Mars:'#CC0000', Rahu:'#4B0082',
  Jupiter:'#7A5200', Saturn:'#000066', Mercury:'#005500', Ketu:'#015A0E', Venus:'#004466',
};

export default function Dashboard() {
  const { user }  = useAuth();
  const navigate  = useNavigate();
  const { t } = useTranslation();
  const [charts,   setCharts]   = useState<Chart[]>([]);
  const [panchang, setPanchang] = useState<Panchang | null>(null);
  const [primary,  setPrimary]  = useState<any>(null);
  const [rashifal, setRashifal] = useState('');
  const [loading,  setLoading]  = useState(true);
  const [activeChart, setActiveChart] = useState(0);
  const [prefs,    setPrefs]    = useState<any>(null);
  const [bannerDismissed, setBannerDismissed] = useState(false);
  const [onboardDismissed, setOnboardDismissed] = useState(false);
  const [birthPanchang, setBirthPanchang] = useState<any>(null);
  const [gochara,  setGochara]  = useState<any>(null);
  const [gocharaLoading, setGocharaLoading] = useState(false);

  const loadGochara = useCallback((chartId: number) => {
    setGochara(null); setGocharaLoading(true);
    const token = sessionStorage.getItem('access_token') || localStorage.getItem('access_token');
    axios.get(`/api/charts/${chartId}/gochara`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => setGochara(r.data))
      .catch(() => {})
      .finally(() => setGocharaLoading(false));
  }, []);

  useEffect(() => {
    axios.get('/api/notifications/preferences').then(r => setPrefs(r.data)).catch(() => {});
    Promise.all([
      axios.get('/api/charts/').catch(() => ({ data: [] })),
      axios.get('/api/panchang').catch(() => ({ data: null })),
    ]).then(([cr, pr]) => {
      const cList: Chart[] = cr.data;
      setCharts(cList);
      setPanchang(pr.data);
      if (cList[0]) {
        axios.get(`/api/charts/${cList[0].id}`).then(r => {
          setPrimary(r.data);
          setBirthPanchang(computePanchangLabels(r.data?.chart));
          axios.get(`/api/rashifal/${cList[0].id}`)
               .then(rf => setRashifal(rf.data.content)).catch(() => {});
        }).catch(() => {});
        loadGochara(cList[0].id);
      }
    }).finally(() => setLoading(false));
  }, [loadGochara]);

  function switchChart(idx: number) {
    setActiveChart(idx);
    const c = charts[idx];
    if (!c) return;
    setPrimary(null);
    setRashifal('');
    setBirthPanchang(null);
    axios.get(`/api/charts/${c.id}`).then(r => {
      setPrimary(r.data);
      setBirthPanchang(computePanchangLabels(r.data?.chart));
      axios.get(`/api/rashifal/${c.id}`)
           .then(rf => setRashifal(rf.data.content)).catch(() => {});
    }).catch(() => {});
    loadGochara(c.id);
  }

  if (loading) return (
    <div className="loading-page">
      <div className="spinner"/>
      <span>{t('dashboard.loading')}</span>
    </div>
  );

  const isFree     = user?.tier === 'free' || user?.tier === 'silver';
  const isGold     = user?.tier === 'pro'  || user?.tier === 'gold';
  const isPlatinum = user?.tier === 'expert' || user?.tier === 'platinum';
  const isPaid     = isGold || isPlatinum;
  const cur        = primary?.chart?.current_dasha;
  const chart      = charts[activeChart];

  // Feature tiles — collapsed to the Phase 3 dynamic-only IA (Today / My Report /
  // Ask AI / Milan / Muhurta / Rectification). Dasha + Yogas are already shown
  // live below (Active Dasha card, Gochara highlight) so those tiles were
  // dropped; Export PDF / Shadbala / Year Forecast consolidated into My Report.
  const tiles = [
    {
      ico: '📄', name: t('dashboard.tiles.myReport.name'),
      sub: t('dashboard.tiles.myReport.sub'),
      action: t('dashboard.tiles.myReport.action'),
      locked: false,
      corner: null,
      onClick: () => navigate('/report'),
    },
    {
      ico: '🔮', name: t('dashboard.tiles.askAi.name'),
      sub: t('dashboard.tiles.askAi.sub'),
      action: t('dashboard.tiles.askAi.action'),
      locked: false,
      corner: null,
      onClick: () => navigate('/ask'),
    },
    // 15 Aug 2026 fix: these three tiles still had the pre-Phase-5
    // hard-lock behavior (redirect to /pricing, never reaching the real
    // page) -- the sidebar (App.tsx LeftSidebar, "Phase 5" comment,
    // 14 Jul 2026) was updated to let every tier navigate to the real
    // page and pay ₹99/use via that page's own BuyCreditModal, but these
    // Dashboard tiles were missed in that change. Same account, same
    // feature, different UI element -> different (wrong) behavior.
    // `locked`/corner-lock kept as visual signal (still shown as a ₹99
    // badge below, not a 🔒), only the navigation target changed.
    {
      ico: '💍', name: t('dashboard.tiles.milan.name'),
      sub: isPaid ? t('dashboard.tiles.milan.subPaid') : t('dashboard.tiles.milan.subLocked'),
      action: isPaid ? t('dashboard.tiles.milan.actionPaid') : t('dashboard.tiles.milan.actionLocked'),
      locked: false,
      corner: !isPaid ? { type: 'lock', text: '₹99' } : null,
      onClick: () => navigate('/milan'),
    },
    {
      ico: '🎯', name: t('dashboard.tiles.muhurta.name'),
      sub: isPlatinum ? t('dashboard.tiles.muhurta.subPaid') : t('dashboard.tiles.muhurta.subLocked'),
      action: isPlatinum ? t('dashboard.tiles.muhurta.actionPaid') : t('dashboard.tiles.muhurta.actionLocked'),
      locked: false,
      corner: !isPlatinum ? { type: 'lock', text: '₹99' } : null,
      onClick: () => navigate('/muhurta'),
    },
    {
      ico: '🕐', name: t('dashboard.tiles.rectify.name'),
      sub: isPlatinum ? t('dashboard.tiles.rectify.subPaid') : t('dashboard.tiles.rectify.subLocked'),
      action: isPlatinum ? t('dashboard.tiles.rectify.actionPaid') : t('dashboard.tiles.rectify.actionLocked'),
      locked: false,
      corner: !isPlatinum ? { type: 'lock', text: '₹99' } : null,
      onClick: () => chart ? navigate(`/chart/${chart.id}/rectify`) : navigate('/dashboard'),
    },
  ];

  return (
    <div>
      {/* Email rashifal awareness banner — show until dismissed */}
      {prefs && !prefs.banner_dismissed && !bannerDismissed && (
        <div className="info-box info-saffron" style={{
          display:'flex', alignItems:'center', justifyContent:'space-between',
          gap:12, marginBottom:12, flexWrap:'wrap'
        }}>
          <span style={{fontSize:13}}>
            🔔 <strong>{t('dashboard.rashifalBanner.title')}</strong> {prefs.email_rashifal ? t('dashboard.rashifalBanner.enabled') : t('dashboard.rashifalBanner.disabled')}
          </span>
          <div style={{display:'flex', gap:8, flexShrink:0}}>
            {!prefs.email_rashifal && (
              <button className="btn btn-primary btn-sm" onClick={async () => {
                await axios.put('/api/notifications/preferences', {email_rashifal: true});
                setPrefs((p: any) => ({...p, email_rashifal: true}));
              }}>{t('dashboard.rashifalBanner.enable')}</button>
            )}
            <button className="btn btn-ghost btn-sm" onClick={async () => {
              setBannerDismissed(true);
              await axios.put('/api/notifications/preferences', {banner_dismissed: true});
            }}>{t('common.gotIt')}</button>
          </div>
        </div>
      )}

      {/* Onboarding guide — 4 steps, shown every login until dismissed */}
      {prefs && !prefs.onboarding_dismissed && !onboardDismissed && (
        <div className="info-box info-teal" style={{ marginBottom:12, padding:'14px 16px' }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', gap:12, marginBottom:10 }}>
            <span style={{ fontSize:13, fontWeight:600, color:'var(--indigo)' }}>
              👋 {t('dashboard.onboarding.title')}
            </span>
            <button className="btn btn-ghost btn-sm" onClick={async () => {
              setOnboardDismissed(true);
              await axios.put('/api/notifications/preferences', {onboarding_dismissed: true});
            }}>{t('common.gotIt')}</button>
          </div>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(180px, 1fr))', gap:10 }}>
            {([
              { n: '1', label: t('dashboard.onboarding.step1'), go: '/chart/new' },
              { n: '2', label: t('dashboard.onboarding.step2'), go: '/report' },
              { n: '3', label: t('dashboard.onboarding.step3'), go: '/ask' },
              { n: '4', label: t('dashboard.onboarding.step4'), go: '/report' },
            ] as { n: string; label: string; go: string }[]).map(step => (
              <div key={step.n} onClick={() => navigate(step.go)}
                   style={{ display:'flex', gap:8, alignItems:'center', cursor:'pointer' }}>
                <div style={{
                  width:22, height:22, borderRadius:'50%', background:'var(--indigo)', color:'#fff',
                  display:'flex', alignItems:'center', justifyContent:'center',
                  fontSize:11, fontWeight:700, flexShrink:0,
                }}>{step.n}</div>
                <span style={{ fontSize:12.5, color:'var(--ink2)' }}>{step.label}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Panchang strip */}
      {panchang && (
        <div className="jt-panch" id="jt-panchang">
          <div className="jt-pc">
            <div className="jt-pc-lbl">{t('dashboard.panchang.vara')}</div>
            <div className="jt-pc-val">{panchang.vara}</div>
          </div>
          <div className="jt-pc">
            <div className="jt-pc-lbl">{t('dashboard.panchang.nakshatra')}</div>
            <div className="jt-pc-val">{panchang.nakshatra}</div>
          </div>
          <div className="jt-pc">
            <div className="jt-pc-lbl">{t('dashboard.panchang.tithi')}</div>
            <div className="jt-pc-val">{panchang.tithi}</div>
          </div>
          <div className="jt-pc">
            <div className="jt-pc-lbl">{t('dashboard.panchang.yoga')}</div>
            <div className="jt-pc-val">{panchang.yoga}</div>
          </div>
          <div className="jt-pc rahuk">
            <div className="jt-pc-lbl">{t('dashboard.panchang.rahukaal')}</div>
            <div className="jt-pc-val">{panchang.rahukaal}</div>
          </div>
          <div className="jt-pc ausp">
            <div className="jt-pc-lbl">{t('dashboard.panchang.auspicious')}</div>
            <div className="jt-pc-val">{panchang.auspicious_time}</div>
          </div>
        </div>
      )}

      {/* Vault chip selector */}
      <div className="jt-vault-row" id="jt-vault">
        <span className="jt-vault-label">{t('dashboard.charts')}:</span>
        {charts.map((c, i) => (
          <div key={c.id}
               className={`jt-vc${i === activeChart ? ' active' : ''}`}
               onClick={() => switchChart(i)}
               title={t('dashboard.clickToSwitch') as string}>
            <div className="jt-vc-av"
                 style={{ background: i === 0 ? 'var(--sfb)' : i === 1 ? 'var(--teal)' : 'var(--indigo)' }}>
              {c.name.charAt(0).toUpperCase()}
            </div>
            {c.name.split(' ')[0]}
            {i === 0 && <span style={{ fontSize: 9, opacity: 0.7, marginLeft: 2 }}>·&nbsp;{t('dashboard.primary')}</span>}
          </div>
        ))}
        {charts.length > 0 && (
          <div className="jt-vc" style={{cursor:'pointer', background:'var(--sfll)',
               border:'1px solid var(--sfb)', color:'var(--sf)', fontWeight:600}}
               onClick={() => navigate('/report')}
               title={t('dashboard.generateReportTitle') as string}>
            {t('nav.myReport')} →
          </div>
        )}
        <div className="jt-vc-add" onClick={() => navigate('/chart/new')}
             title={t('dashboard.addNewChart') as string}>+</div>
        {charts.length === 0 && (
          <span style={{ fontSize: 12, color: 'var(--ink3)', marginLeft: 4 }}>
            {t('dashboard.noChartsYet')} —
            <span style={{ color: 'var(--sf)', cursor: 'pointer', marginLeft: 4 }}
                  onClick={() => navigate('/chart/new')}>
              {t('dashboard.createFirstKundali')}
            </span>
          </span>
        )}
      </div>

      {/* Feature tiles */}
      <div className="jt-tiles">
        {tiles.map((tl) => (
          <div key={tl.name}
               className={`jt-tile${tl.locked ? ' locked' : ''}`}
               onClick={tl.onClick}>
            <div className="jt-tile-ico">{tl.ico}</div>
            <div className="jt-tile-name">{tl.name}</div>
            <div className="jt-tile-sub">{tl.sub}</div>
            <div className="jt-tile-action">{tl.action}</div>
            {tl.corner && (
              <div className={`jt-tile-corner ${tl.corner.type}`}
                   style={(tl.corner as any).color ? { color: (tl.corner as any).color } : {}}>
                {tl.corner.text}
              </div>
            )}
          </div>
        ))}
      </div>

      {/* Bottom split: Dasha + Rashifal + Birth Panchang + Gochara highlight */}
      <div className="jt-split">
        {/* Active Dasha */}
        <div className="jt-scard">
          <div className="jt-scard-h">⭐ {t('dashboard.activeDasha')}{chart ? ` — ${chart.name}` : ''}</div>
          {cur ? (
            [
              { label: t('dashboard.mahadasha'), lord: cur.md, end: cur.md_end },
              { label: t('dashboard.antardasha'), lord: cur.ad, end: cur.ad_end },
              { label: t('dashboard.pratyantar'), lord: cur.pd, end: cur.pd_end },
            ].map(({ label, lord, end }) => (
              <div key={label} className="jt-drow">
                <div className="jt-dpill"
                     style={{ background: DASHA_BG[lord] || '#555' }}>
                  {lord}
                </div>
                <div className="jt-dmeta">{label} · {t('dashboard.until')} {end}</div>
              </div>
            ))
          ) : (
            <div style={{ color: 'var(--ink3)', fontSize: 12, padding: '8px 0' }}>
              {charts.length === 0
                ? t('dashboard.createChartForDasha')
                : t('dashboard.loadingDasha')}
            </div>
          )}
        </div>

        {/* Today's Prediction */}
        <div className="jt-scard">
          {rashifal ? (
            <>
              <div className="jt-rf-badge">
                🔮 {t('dashboard.todaysPrediction')}
                {cur && <span style={{ opacity: 0.7, marginLeft: 4 }}>· {cur.md}/{cur.ad}</span>}
              </div>
              <div className="jt-rf-text">{rashifal}</div>
            </>
          ) : (
            <div style={{ color: 'var(--ink3)', fontSize: 12, padding: '8px 0' }}>
              {charts.length === 0
                ? t('dashboard.createChartForPredictions')
                : primary ? t('dashboard.generatingPrediction') : t('common.loading')}
            </div>
          )}
        </div>

        {/* Birth Panchang context */}
        <div className="jt-scard">
          <div className="jt-scard-h">📅 {t('dashboard.birthPanchang')}{chart ? ` — ${chart.name}` : ''}</div>
          {birthPanchang ? (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6, fontSize: 12 }}>
              {[
                [t('dashboard.panchang.vara'), `${birthPanchang.vara} (${birthPanchang.varaLord})`],
                [t('dashboard.panchang.tithi'), `${birthPanchang.tithi}`],
                [t('dashboard.paksha'), birthPanchang.paksha.split(' ')[0]],
                [t('dashboard.panchang.nakshatra'), birthPanchang.nakshatra],
                [t('dashboard.panchang.yoga'), birthPanchang.yoga],
                [t('dashboard.karana'), birthPanchang.karana],
              ].map(([label, val]) => (
                <div key={label}>
                  <div style={{ fontSize: 9, color: 'var(--ink3)', textTransform: 'uppercase' }}>{label}</div>
                  <div style={{ fontWeight: 600, color: 'var(--ink)' }}>{val}</div>
                </div>
              ))}
            </div>
          ) : (
            <div style={{ color: 'var(--ink3)', fontSize: 12, padding: '8px 0' }}>
              {charts.length === 0 ? t('dashboard.createChartForPanchang') : t('common.loading')}
            </div>
          )}
        </div>

        {/* Live Gochara / current-transits highlight */}
        <div className="jt-scard">
          <div className="jt-scard-h">🌍 {t('dashboard.currentTransits')}{gochara?.as_of ? ` — ${t('dashboard.asOf')} ${gochara.as_of}` : ''}</div>
          {gocharaLoading ? (
            <div style={{ color: 'var(--ink3)', fontSize: 12, padding: '8px 0' }}>{t('dashboard.computingTransits')}</div>
          ) : gochara ? (
            <>
              <div style={{ fontSize: 12, color: 'var(--ink2)', marginBottom: 8 }}>
                {t('dashboard.natalMoon')}: <strong>{gochara.natal_moon?.nakshatra}</strong> ({gochara.natal_moon?.nak_lord}) {t('dashboard.inSign')} {gochara.natal_moon?.sign}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {(['favorable', 'unfavorable', 'mixed'] as const).map(nature => {
                  const names = gochara.tara_summary?.[nature] || [];
                  if (!names.length) return null;
                  const color = nature === 'favorable' ? 'var(--teal)' : nature === 'unfavorable' ? 'var(--red)' : 'var(--sf)';
                  const natureLabel = nature === 'favorable' ? t('dashboard.tara.favorable') : nature === 'unfavorable' ? t('dashboard.tara.unfavorable') : t('dashboard.tara.mixed');
                  return (
                    <span key={nature} title={`${natureLabel} ${t('dashboard.tara.label')}`} style={{
                      fontSize: 11, fontWeight: 600, color, background: color + '18',
                      border: `1px solid ${color}44`, borderRadius: 5, padding: '2px 7px',
                    }}>
                      {names.join(', ')}
                    </span>
                  );
                })}
              </div>
              {gochara.narrative && (
                // v4.1, BLUEPRINT.md Future Phases -- Transit Forecast
                // Narrative. AI text comes back already in the user's
                // language (backend uses report_language), so no new
                // translation key needed here -- avoids reopening all 19
                // locale files for a single label.
                <div style={{
                  fontSize: 12.5, color: 'var(--ink2)', marginTop: 10,
                  paddingTop: 10, borderTop: '1px solid var(--border, #e5e0d8)',
                  lineHeight: 1.5, fontStyle: 'italic',
                }}>
                  {gochara.narrative}
                </div>
              )}
            </>
          ) : (
            <div style={{ color: 'var(--ink3)', fontSize: 12, padding: '8px 0' }}>
              {charts.length === 0 ? t('dashboard.createChartForTransits') : isFree ? t('dashboard.upgradeForTransits') : t('common.loading')}
            </div>
          )}
        </div>
      </div>

      {/* Upgrade nudge for free users */}
      {isFree && charts.length > 0 && (
        <div className="info-box info-saffron" style={{ marginTop: 12 }}>
          <strong>{t('dashboard.freePlanNudge.title')}</strong> {t('dashboard.freePlanNudge.body')} &nbsp;
          <a href="/pricing" style={{ fontWeight: 600 }}>{t('dashboard.freePlanNudge.cta')}</a>
        </div>
      )}
    </div>
  );
}
